import { useState, useEffect, useCallback, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";

import type { AppDispatch, RootState } from "../../store/store";
import {
  fetchLabByToken,
  fetchPartnerLabDetails,
  clearLab,
} from "../../store/admin/labDetailsSlice";
import { useAppSelector } from "../../store/hooks";
import {
  AdminPageShell,
  AdminPageHeader,
  AdminTableCard,
  AdminSoftInput,
  AdminPrimaryButton,
  adminTableHead,
  adminTableCell,
} from "../../components/admin/AdminPageShell";
import LabPreferenceScreenshots from "../../components/admin/LabPreferenceScreenshots";
import { isUrisBrand } from "../../utils/brandLogo";

/* -------------------- Subcomponents -------------------- */
const InfoRow = memo(
  ({ label, value, uris }: { label: string; value: any; uris?: boolean }) => (
    <p className={uris ? "text-[#c5c7cc]" : "text-[#555552]"}>
      <span className={uris ? "font-semibold text-white" : "text-[#1A1A1A] font-semibold"}>
        {label}:
      </span>{" "}
      {value ?? "—"}
    </p>
  ),
);

const PreferencesList = memo(
  ({ title, entries }: { title: string; entries: [string, any][] }) => {
    if (!entries.length) return null;
    return (
      <div className="mt-1 md:mt-2">
        <strong>{title}:</strong>
        <ul className="ml-2 md:ml-4 list-disc">
          {entries.map(([key, value]) => (
            <li key={key}>
              {key}: {String(value)}
            </li>
          ))}
        </ul>
      </div>
    );
  },
);

const PreferencesSection = memo(({ raw, uris }: { raw: string; uris?: boolean }) => {
  const prefs = useMemo(() => {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [raw]);

  if (!prefs) return <p>Lab Preferences: null</p>;

  const filterEntries = (obj: Record<string, any>) =>
    Object.entries(obj || {}).filter(
      ([, value]) => value !== null && value !== "" && value !== false,
    );

  const parameters = filterEntries(prefs.parameters);
  const anatomy = filterEntries(prefs.anatomy);
  const software = filterEntries(prefs.software);
  const libraryPreference = filterEntries(prefs.library_preference);
  const modelPreference = filterEntries(prefs.modelPreference);
  const filesPreference = filterEntries(prefs.files_preference);
  const otherPreference = filterEntries(prefs.otherPreference);

  if (
    !parameters.length &&
    !anatomy.length &&
    !software.length &&
    !libraryPreference.length &&
    !modelPreference.length &&
    !filesPreference.length &&
    !otherPreference.length
  ) {
    return <p>Lab Preferences: null</p>;
  }

  return (
    <div
      className={`mt-3 md:mt-4 p-3 md:p-4 rounded-2xl border ${
        uris
          ? "border-[#2e3036] bg-[#1a1b1f] text-[#c5c7cc]"
          : "border-[#E8E8E4] bg-[#F8F8F6]"
      }`}
    >
      <h3 className={`font-semibold ${uris ? "text-white" : "text-[#4A4A48]"}`}>
        Lab Preferences
      </h3>
      <PreferencesList title="Parameters" entries={parameters} />
      <PreferencesList title="Anatomy" entries={anatomy} />
      <PreferencesList title="Software" entries={software} />
      <PreferencesList title="Library Preference" entries={libraryPreference} />
      <PreferencesList title="Model Preference" entries={modelPreference} />
      <PreferencesList title="Files Preference" entries={filesPreference} />
      <PreferencesList title="Other Preference" entries={otherPreference} />
    </div>
  );
});

const PartnerLabsTable = memo(
  ({
    labs,
    onSelect,
    uris,
  }: {
    labs: any[];
    onSelect: (lab: any) => void;
    uris?: boolean;
  }) => {
    if (!labs.length)
      return (
        <div className="py-14 text-center text-[#8A8A85]">
          No labs are there now
        </div>
      );

    return (
      <AdminTableCard
        className={
          uris
            ? "overflow-x-auto !border-[#2e3036] !bg-[#1a1b1f] shadow-none"
            : ""
        }
      >
        <table className="w-full table-auto">
          <thead>
            <tr
              className={
                uris
                  ? "border-b border-[#2e3036]"
                  : "border-b border-[#E8E8E4] bg-[#FDFBF2]"
              }
            >
              <th className={`${adminTableHead}${uris ? " !text-[#8b8d93]" : ""}`}>
                Lab Token
              </th>
              <th className={`${adminTableHead}${uris ? " !text-[#8b8d93]" : ""}`}>
                Name
              </th>
              <th className={`${adminTableHead}${uris ? " !text-[#8b8d93]" : ""}`}>
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {labs.map((labItem) => (
              <tr
                key={labItem.token}
                className={`border-b last:border-0 transition-colors ${
                  uris
                    ? "border-[#2e3036] text-[#c5c7cc] hover:bg-[#1d1e22]"
                    : "border-[#F0F0EC] hover:bg-[#FAFAF8]"
                }`}
              >
                <td className={`${adminTableCell}${uris ? " !text-[#c5c7cc]" : ""}`}>
                  {labItem.token}
                </td>
                <td className={`${adminTableCell}${uris ? " !text-[#c5c7cc]" : ""}`}>
                  {labItem.user_name}
                </td>
                <td className={`${adminTableCell}${uris ? " !text-[#c5c7cc]" : ""}`}>
                  <button
                    type="button"
                    className={
                      uris
                        ? "font-medium text-[#ff8b96] hover:text-[#ffb0b8] hover:underline"
                        : "text-[#2fa9bf] hover:text-[#2296aa] hover:underline font-medium"
                    }
                    onClick={() => onSelect(labItem)}
                  >
                    See Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminTableCard>
    );
  },
);

const SearchBar = memo(
  ({
    searchToken,
    setSearchToken,
    onSearch,
    onAddPreferences,
    canEditPreferences,
    uris,
  }: {
    searchToken: string;
    setSearchToken: (val: string) => void;
    onSearch: () => void;
    onAddPreferences: () => void;
    canEditPreferences: boolean;
    uris?: boolean;
  }) => (
    <div className="flex flex-col md:flex-row gap-2 mb-4">
      <AdminSoftInput
        type="text"
        value={searchToken}
        onChange={(e) => setSearchToken(e.target.value.toUpperCase())}
        placeholder="Enter lab token"
        className={
          uris
            ? "flex-1 uppercase !border-[#3a3c42] !bg-[#141517] !text-white placeholder:!text-[#8b8d93] focus:!border-[#c31c30] focus:!ring-[#c31c30]/20"
            : "flex-1 uppercase"
        }
      />
      {uris ? (
        <button
          type="button"
          onClick={onSearch}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#e11d2e] text-white text-xs font-medium hover:bg-[#c31c30]"
        >
          Find Labs
        </button>
      ) : (
        <AdminPrimaryButton onClick={onSearch}>Find Labs</AdminPrimaryButton>
      )}
      {canEditPreferences ? (
        <button
          type="button"
          onClick={onAddPreferences}
          disabled={!searchToken.trim()}
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add Preferences
        </button>
      ) : null}
    </div>
  ),
);

const LabCard = memo(
  ({
    lab,
    onBack,
    preferencesOnly,
    canUploadScreenshots,
    uris,
  }: {
    lab: any;
    onBack: () => void;
    preferencesOnly: boolean;
    canUploadScreenshots: boolean;
    uris?: boolean;
  }) => (
    <div
      className={`p-4 md:p-6 rounded-2xl border mt-4 space-y-2 ${
        uris
          ? "border-[#6b2430] bg-[#141517] shadow-none"
          : "border-[#E8E8E4] bg-white shadow-sm"
      }`}
    >
      <div className="flex justify-between items-center pb-2">
        <h2
          className={`text-base md:text-lg font-bold ${
            uris ? "text-white" : "text-[#4A4A48]"
          }`}
        >
          {lab.user_name || lab.token || "Lab"}
        </h2>
        <button
          type="button"
          onClick={onBack}
          className={`px-3 py-1.5 rounded-xl border text-xs font-medium transition ${
            uris
              ? "border-[#3a3c42] bg-[#1a1b1f] text-[#c5c7cc] hover:border-[#c31c30]"
              : "border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6]"
          }`}
        >
          Back
        </button>
      </div>

      {preferencesOnly ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-2 md:gap-2">
          <InfoRow label="Lab" value={lab.user_name} uris={uris} />
          <InfoRow label="Token" value={lab.token} uris={uris} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mt-2 md:gap-2">
          {[
            ["ID", lab.user_id],
            ["Email", lab.email],
            ["Phone", lab.phone_number],
            ["Address", lab.address],
            ["Organisation", lab.organisation],
            ["Organisation Type", lab.organisation_type],
            ["Post Code", lab.post_code],
            ["Token", lab.token],
            ["Partner", lab.partner ? "Yes" : "No"],
          ].map(([label, value]) => (
            <InfoRow key={label} label={label} value={value} uris={uris} />
          ))}
        </div>
      )}

      {lab.lab_preferences && (
        <PreferencesSection raw={lab.lab_preferences} uris={uris} />
      )}
      {lab.token ? (
        <LabPreferenceScreenshots
          token={lab.token}
          canUpload={canUploadScreenshots}
          dark={uris}
        />
      ) : null}
    </div>
  ),
);

/* -------------------- Main Component -------------------- */
const LabDetails = () => {
  const [searchToken, setSearchToken] = useState("");
  const [searchAttempted, setSearchAttempted] = useState(false);

  const { user } = useAppSelector((state) => state.auth);
  const uris = isUrisBrand(user);
  const isProduction = user?.role === "Production";
  const canEditPreferences = ["Super Admin", "Admin", "Operations"].includes(
    user?.role || "",
  );
  const canUploadScreenshots = canEditPreferences;
  const dispatch = useDispatch<AppDispatch>();
  const { lab, labs, isLoading, error } = useSelector(
    (state: RootState) => state.labDetails,
  );

  const handleFetch = useCallback(() => {
    const trimmed = searchToken.trim();
    if (!trimmed) return;
    setSearchAttempted(true);
    dispatch(fetchLabByToken(trimmed));
  }, [searchToken, dispatch]);

  const handleBack = useCallback(() => {
    dispatch(clearLab());
    setSearchToken("");
    setSearchAttempted(false);
  }, [dispatch]);

  useEffect(() => {
    if (user?.partner && !isProduction) {
      dispatch(fetchPartnerLabDetails(user.token));
    }
  }, [user?.partner, user?.token, isProduction, dispatch]);

  const handleAddPreferences = useCallback(() => {
    const trimmed = searchToken.trim();
    if (!trimmed) return;

    window.open(
      `https://www.toothsketch.com/dashboard/admin/labpreference/${trimmed}`,
      "_blank"
    );
  }, [searchToken]);

  return (
    <>
      <Helmet>
        <title>{isProduction ? "Lab Preferences" : "Lab Details"}</title>
      </Helmet>

      <AdminPageShell accent={uris ? "#141517" : undefined}>
        {uris ? (
          <div className="rounded-2xl border border-[#6b2430] bg-[#141517] px-4 py-4 md:px-6">
            <h1 className="text-lg sm:text-xl font-bold text-white">
              {isProduction ? "Lab Preferences" : "Lab Details"}
            </h1>
            <p className="text-[#c5c7cc] text-xs sm:text-sm mt-0.5">
              {isProduction
                ? "Look up lab design preferences by token"
                : "Look up lab profiles and preferences"}
            </p>
          </div>
        ) : (
          <AdminPageHeader
            title={isProduction ? "Lab Preferences" : "Lab Details"}
            subtitle={
              isProduction
                ? "Look up lab design preferences by token"
                : "Look up lab profiles and preferences"
            }
          />
        )}

        {/* Partner Labs Table */}
        {user?.partner && !isProduction && !lab && (
          <PartnerLabsTable
            labs={labs}
            onSelect={(labItem) => dispatch(fetchLabByToken(labItem.token))}
            uris={uris}
          />
        )}

        {/* Search for labs if not a partner */}
        {(!user?.partner || isProduction) && !lab && (
          <SearchBar
            searchToken={searchToken}
            setSearchToken={setSearchToken}
            onSearch={handleFetch}
            onAddPreferences={handleAddPreferences}
            canEditPreferences={canEditPreferences}
            uris={uris}
          />
        )}

        {/* Loading & Errors */}
        {isLoading && (
          <p className={uris ? "text-[#ff8b96]" : "text-[#2fa9bf]"}>Loading...</p>
        )}
        {error && <p className="text-red-500">{error}</p>}
        {searchAttempted && !isLoading && !lab && !error && (
          <p className="text-red-500">Lab not found with this token.</p>
        )}

        {/* Lab Details */}
        {lab && (
          <LabCard
            lab={lab}
            onBack={handleBack}
            preferencesOnly={isProduction}
            canUploadScreenshots={canUploadScreenshots}
            uris={uris}
          />
        )}
      </AdminPageShell>
    </>
  );
};

export default LabDetails;
